import React, { useState, useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import AwakenCard from "../Popup/AwakenCard";
import { frontendLogger } from '../services/frontendLogger.js';
import { detectInjection } from '../utils/injectionDetector.js';
import { urlConfig } from '../config/urlConfig.js';

// File validation constants
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_FILE_TYPES = [
  'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
  'application/pdf', 'text/plain',
  'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.pdf', '.txt', '.doc', '.docx'];

// Enhanced file validation for security
const FILE_MAGIC_HEADERS = {
  'image/jpeg': ['FFD8FF'],
  'image/png': ['89504E47'],
  'image/gif': ['474946'],
  'application/pdf': ['255044462D']
};

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    reason: "Support",
    customReason: "",
    subject: "",
    message: "",
    file: null,
    agree: false,
    // Honeypot fields (hidden from users, filled by bots)
    website: "", // Common bot trap
    company: "", // Another bot trap
    phone: "" // Third bot trap
  });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Bot protection tracking
  const [formStartTime] = useState(Date.now());
  const [interactionCount, setInteractionCount] = useState(0);
  const [keyboardEvents, setKeyboardEvents] = useState(0);
  const [mouseEvents, setMouseEvents] = useState(0);
  const [securityWarnings, setSecurityWarnings] = useState({});

  // Auto-dismiss success messages after 45 seconds
  useEffect(() => {
    if (status.type === 'success' && status.message) {
      const timer = setTimeout(() => {
        setStatus({ type: '', message: '' });
      }, 45000); // 45 seconds
      
      return () => clearTimeout(timer);
    }
  }, [status]);

  const handleChange = async (e) => {
    const { name, value, type, checked, files } = e.target;
    
    // Track user interactions for bot detection
    setInteractionCount(prev => prev + 1);
    
    // Honeypot field detection (should always be empty)
    if (['website', 'company', 'phone'].includes(name) && value) {
      // Silent rejection - don't show error to avoid tipping off bots
      frontendLogger.warn('Bot detected - honeypot field filled', {
        component: 'ContactUs',
        action: 'validateFormData'
      });
      return;
    }
    
    if (type === 'file' && files && files[0]) {
      const file = files[0];
      
      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        setStatus({ 
          type: "error", 
          message: `File size too large. Maximum allowed size is ${Math.round(MAX_FILE_SIZE / (1024 * 1024))}MB. Your file is ${Math.round(file.size / (1024 * 1024))}MB.` 
        });
        e.target.value = ''; // Clear the input
        return;
      }
      
      // Enhanced file validation with security checks
      const enhancedFileValidation = async (file) => {
        // 1. Validate file extension matches MIME type
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        if (!ALLOWED_FILE_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(fileExtension)) {
          throw new Error('Invalid file type. Please upload only: Images (JPG, PNG, GIF, WebP), PDFs, Text files, or Word documents.');
        }
        
        // 2. Check for double extensions (security risk)
        const fileName = file.name.toLowerCase();
        const suspiciousExtensions = ['.exe', '.bat', '.cmd', '.com', '.pif', '.scr', '.vbs', '.js', '.jar', '.php', '.asp', '.jsp'];
        if (suspiciousExtensions.some(ext => fileName.includes(ext))) {
          throw new Error('File contains suspicious extensions and cannot be uploaded.');
        }
        
        // 3. Verify file header matches declared MIME type (magic number check)
        if (FILE_MAGIC_HEADERS[file.type]) {
          const arrayBuffer = await file.slice(0, 10).arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);
          const fileHeader = Array.from(uint8Array).map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase();
          
          const validHeaders = FILE_MAGIC_HEADERS[file.type];
          const isValidHeader = validHeaders.some(header => fileHeader.startsWith(header));
          
          if (!isValidHeader) {
            throw new Error('File header does not match declared file type. This may indicate file tampering.');
          }
        }
        
        // 4. Basic content scanning for suspicious patterns
        if (file.type === 'text/plain') {
          const text = await file.text();
          const maliciousPatterns = [
            /<script[^>]*>/i,
            /javascript:/i,
            /vbscript:/i,
            /@echo\s+off/i,
            /powershell/i,
            /cmd\.exe/i,
            /eval\s*\(/i
          ];
          
          if (maliciousPatterns.some(pattern => pattern.test(text))) {
            throw new Error('Text file contains potentially malicious content.');
          }
        }
        
        return true;
      };
      
      try {
        await enhancedFileValidation(file);
      } catch (validationError) {
        setStatus({ 
          type: "error", 
          message: validationError.message 
        });
        e.target.value = ''; // Clear the input
        return;
      }
      
      // Validate file type (basic check)
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      if (!ALLOWED_FILE_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(fileExtension)) {
        setStatus({ 
          type: "error", 
          message: `Invalid file type. Please upload only: Images (JPG, PNG, GIF, WebP), PDFs, Text files, or Word documents.` 
        });
        e.target.value = ''; // Clear the input
        return;
      }
      
      // Clear any previous file-related errors
      setStatus({ type: "", message: "" });
    }
    
    // Real-time security validation for text fields (non-intrusive)
    if (type === 'text' || type === 'email' || name === 'message' || name === 'subject') {
      const securityCheck = validateTextSecurity(name, value);
      if (!securityCheck.isClean) {
        // Update security warnings state
        setSecurityWarnings(prev => ({
          ...prev,
          [name]: {
            threats: securityCheck.threats,
            riskLevel: securityCheck.riskLevel
          }
        }));
        
        if (securityCheck.riskLevel === 'high') {
          // Log high-risk attempt
          frontendLogger.warn(`High-risk content detected during typing in ${name}`, {
            fieldName: name,
            threats: securityCheck.threats,
            riskLevel: securityCheck.riskLevel
          }, 'ContactUs');
        }
      } else {
        // Clear warnings for this field if content is now clean
        setSecurityWarnings(prev => {
          const updated = { ...prev };
          delete updated[name];
          return updated;
        });
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : type === "file" ? files[0] : value,
    }));
  };

  // Track keyboard events for behavioral analysis
  const handleKeyDown = () => {
    setKeyboardEvents(prev => prev + 1);
  };
  
  // Track mouse events for behavioral analysis
  const handleMouseMove = () => {
    setMouseEvents(prev => prev + 1);
  };
  
  // HTML entity decoder function
  const decodeHtmlEntities = (text) => {
    const entityMap = {
      '&amp;': '&',
      '&lt;': '<',
      '&gt;': '>',
      '&quot;': '"',
      '&#x27;': "'",
      '&#39;': "'",
      '&#x2F;': '/',
      '&#47;': '/',
      '&#x5C;': '\\',
      '&#92;': '\\',
      '&#x60;': '`',
      '&#96;': '`'
    };
    
    return text.replace(/&(?:#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (entity) => {
      if (entityMap[entity]) {
        return entityMap[entity];
      }
      // Handle numeric character references
      if (entity.startsWith('&#x')) {
        const hex = entity.slice(3, -1);
        return String.fromCharCode(parseInt(hex, 16));
      } else if (entity.startsWith('&#')) {
        const dec = entity.slice(2, -1);
        return String.fromCharCode(parseInt(dec, 10));
      }
      return entity;
    });
  };
  
  // Text field security validation
  const validateTextSecurity = (fieldName, text) => {
    if (!text || typeof text !== 'string') return { isClean: true };
    
    // Decode HTML entities to catch encoded attacks
    const decodedText = decodeHtmlEntities(text);
    
    // Special handling for email fields - use email-specific validation
    if (fieldName === 'email') {
      // Check if it's a valid email format first
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(decodedText.trim())) {
        // For valid email addresses, only check for script injections
        const emailThreats = [];
        const normalizedText = decodedText.toLowerCase();
        
        // Only check for dangerous patterns that could affect emails
        const emailAttackPatterns = [
          /javascript\s*:/i,
          /<script/i,
          /eval\s*\(/i,
          /\bon\w+\s*=/i,
          /<[^>]+>/i // Any HTML tags
        ];
        
        for (const pattern of emailAttackPatterns) {
          if (pattern.test(normalizedText)) {
            const match = normalizedText.match(pattern);
            emailThreats.push({
              type: 'email_injection',
              match: match[0],
              severity: 'high'
            });
          }
        }
        
        if (emailThreats.length > 0) {
          frontendLogger.warn(`Email field injection attempt detected`, {
            fieldName,
            threats: emailThreats,
            email: decodedText
          }, 'ContactUs');
          
          return {
            isClean: false,
            threats: emailThreats,
            riskLevel: 'high'
          };
        }
        
        // Valid email with no threats
        return { isClean: true };
      }
    }
    
    // Use comprehensive injection detector for non-email fields
    const securityCheck = detectInjection(decodedText);
    
    if (!securityCheck.isClean) {
      frontendLogger.warn(`Contact form security threat detected in ${fieldName}`, {
        fieldName,
        threats: securityCheck.threats,
        riskLevel: securityCheck.riskLevel,
        originalLength: text.length,
        decodedLength: decodedText.length
      }, 'ContactUs');
    }
    
    return securityCheck;
  };
  
  // Bot protection validation
  const validateHumanBehavior = () => {
    const submissionTime = Date.now();
    const timeSpent = submissionTime - formStartTime;
    
    // Check for honeypot fields
    if (formData.website || formData.company || formData.phone) {
      throw new Error('Suspicious activity detected. Please contact support if you believe this is an error.');
    }
    
    // Check submission timing (too fast = bot)
    if (timeSpent < 4000) { // Less than 4 seconds
      throw new Error('Form submitted too quickly. Please take your time filling out the form.');
    }
    
    // Check for minimum interactions
    if (interactionCount < 5) {
      throw new Error('Insufficient form interaction detected. Please fill out the form completely.');
    }
    
    // Check for some keyboard/mouse activity (basic behavioral analysis)
    if (keyboardEvents < 10 && mouseEvents < 5) {
      throw new Error('Please interact with the form normally to verify you are human.');
    }
    
    return true;
  };

  const removeFile = () => {
    setFormData((prev) => ({ ...prev, file: null }));
    const fileInput = document.getElementById('file-upload');
    if (fileInput) fileInput.value = '';
    setStatus({ type: "", message: "" }); // Clear any file-related errors
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    // Comprehensive client-side validation
    const errors = [];
    const securityThreats = [];
    
    if (!formData.agree) {
      errors.push("You must agree to our terms to continue.");
    }
    
    // Security validation for all text fields
    const fieldsToValidate = [
      { name: 'name', value: formData.name },
      { name: 'email', value: formData.email },
      { name: 'subject', value: formData.subject },
      { name: 'message', value: formData.message },
      { name: 'customReason', value: formData.customReason }
    ];
    
    for (const field of fieldsToValidate) {
      const securityCheck = validateTextSecurity(field.name, field.value);
      if (!securityCheck.isClean) {
        securityThreats.push({
          field: field.name,
          threats: securityCheck.threats,
          riskLevel: securityCheck.riskLevel
        });
        
        if (securityCheck.riskLevel === 'high') {
          errors.push(`Security threat detected in ${field.name}. Please remove suspicious content.`);
        }
      }
    }
    
    // Block submission if high-risk security threats found
    if (securityThreats.some(threat => threat.riskLevel === 'high')) {
      errors.push("Your submission contains potentially dangerous content and has been blocked for security reasons.");
    }
    
    if (!formData.name.trim()) {
      errors.push("Name is required.");
    } else if (formData.name.trim().length < 2) {
      errors.push("Name must be at least 2 characters long.");
    } else if (formData.name.trim().length > 100) {
      errors.push("Name must be less than 100 characters.");
    }
    
    if (!formData.email.trim()) {
      errors.push("Email address is required.");
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        errors.push("Please enter a valid email address.");
      }
    }
    
    if (!formData.subject.trim()) {
      errors.push("Subject is required.");
    } else if (formData.subject.trim().length < 3) {
      errors.push("Subject must be at least 3 characters long.");
    } else if (formData.subject.trim().length > 200) {
      errors.push("Subject must be less than 200 characters.");
    }
    
    if (!formData.message.trim()) {
      errors.push("Message is required.");
    } else if (formData.message.trim().length < 10) {
      errors.push("Please provide a more detailed message (at least 10 characters).");
    } else if (formData.message.trim().length > 5000) {
      errors.push("Message must be less than 5000 characters.");
    }
    
    if (formData.reason === 'Other' && !formData.customReason.trim()) {
      errors.push("Please specify your custom reason.");
    }
    
    // File validation (if file is selected)
    if (formData.file) {
      if (formData.file.size > MAX_FILE_SIZE) {
        errors.push(`File size too large. Maximum allowed size is ${Math.round(MAX_FILE_SIZE / (1024 * 1024))}MB.`);
      }
      
      const fileExtension = '.' + formData.file.name.split('.').pop().toLowerCase();
      if (!ALLOWED_FILE_TYPES.includes(formData.file.type) && !ALLOWED_EXTENSIONS.includes(fileExtension)) {
        errors.push("Invalid file type. Please upload only images, PDFs, text files, or Word documents.");
      }
    }
    
    if (errors.length > 0) {
      setStatus({ 
        type: "error", 
        message: errors.length === 1 ? errors[0] : `Please fix the following issues:\n• ${errors.join('\n• ')}` 
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Bot protection validation
      validateHumanBehavior();
      
      // Use CSRF service for secure submission
      const submitData = new FormData();
      submitData.append('name', formData.name.trim());
      submitData.append('email', formData.email.trim());
      submitData.append('reason', formData.reason === 'Other' ? formData.customReason.trim() : formData.reason);
      submitData.append('subject', formData.subject.trim());
      submitData.append('message', formData.message.trim());
      
      if (formData.file) {
        submitData.append('file', formData.file);
      }

      // Submit to public contact endpoint (no auth/CSRF needed)
      const response = await fetch(`${urlConfig.api.baseURL}/api/contact`, {
        method: 'POST',
        body: submitData,
        credentials: 'include' // Include cookies for rate limiting
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      setStatus({
        type: "success",
        message: "Thank you for reaching out! Your message has been received and we'll respond within 24-48 hours. A confirmation email has been sent to your address.",
      });
      
      // Reset form
      setFormData({
        name: "",
        email: "",
        reason: "Support",
        customReason: "",
        subject: "",
        message: "",
        file: null,
        agree: false,
        // Reset honeypot fields to prevent controlled/uncontrolled input error
        website: "",
        company: "",
        phone: ""
      });
      
      // Reset file input
      const fileInput = document.getElementById('file-upload');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      frontendLogger.error('Contact form error', {
        component: 'ContactUs',
        action: 'handleSubmit',
        error: error.message,
        stack: error.stack
      });
      
      let errorMessage = "We're experiencing technical difficulties. Please try again later or email us directly at support@andlegur.com";
      
      // Handle specific error types
      if (error.message) {
        if (error.message.includes('rate limit')) {
          errorMessage = "You're sending messages too quickly. Please wait a few minutes before trying again.";
        } else if (error.message.includes('file')) {
          errorMessage = error.message; // File-specific errors
        } else if (error.message.includes('validation')) {
          errorMessage = "Please check your input and try again. Make sure all required fields are filled correctly.";
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          errorMessage = "Network connection error. Please check your internet connection and try again.";
        } else {
          errorMessage = error.message;
        }
      }
      
      setStatus({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-12 px-6 lg:px-8">
      <Helmet>
        <title>Contact Andlegur | Get in Touch & Connect With Us</title>
        <meta name="description" content="Have questions or feedback? Contact the Andlegur team. We're here to help you on your journey of reflection and personal growth." />
        <link rel="canonical" href="https://andlegur.com/contact-us" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <div className="max-w-3xl mx-auto shadow-xl border-2 border-indigo-300 rounded-2xl p-4">
        <h1 className="text-3xl font-bold text-indigo-200 mb-6 text-center">
          Contact Andlegur
        </h1>
        <p className="text-indigo-100 text-center mb-8">
          We welcome your voice — whether it is a question, a call for
          collaboration, or a blessing. Share with us, and we will walk with you
          in your journey.
        </p>

        {status.message && (
          <div
            className={`mb-6 p-4 rounded-lg border font-medium ${
              status.type === "success"
                ? "bg-green-900/20 border-green-500/30 text-green-300 text-center"
                : "bg-red-900/20 border-red-500/30 text-red-300 text-left"
            }`}
          >
            <div className="flex items-start">
              {status.type === "success" && (
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
              {status.type === "error" && (
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              )}
              <div className="flex-1">
                {status.message.includes('\n') ? (
                  <div className="whitespace-pre-line">{status.message}</div>
                ) : (
                  <div className={status.type === "success" ? "text-center" : ""}>{status.message}</div>
                )}
              </div>
            </div>
          </div>
        )}

        <form
          className="space-y-6"
          onSubmit={handleSubmit}
          encType="multipart/form-data"
          onKeyDown={handleKeyDown}
          onMouseMove={handleMouseMove}
        >
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-indigo-50 mb-6">Your Name *</label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              maxLength="100"
              className="mt-1 block w-full rounded-lg border border-indigo-300 bg-[#0A1030] text-indigo-100 placeholder-indigo-400 placeholder:text-sm h-14 pl-2 shadow-sm focus:ring-indigo-50 focus:border-indigo-200 sm:text-sm"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-indigo-50 mb-6">Email Address *</label>
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="you@email.com"
              maxLength="120"
              className="mt-1 block w-full rounded-lg border border-indigo-300 bg-[#0A1030] text-indigo-100 placeholder-indigo-400 placeholder:text-sm h-14 pl-2 shadow-sm focus:ring-indigo-50 focus:border-indigo-200 sm:text-sm"
            />
          </div>

          {/* Dropdown */}
          <div>
            <label className="block text-sm font-medium text-indigo-100 mb-6">Why are you reaching out? *</label>
            <select
              name="reason"
              value={formData.reason}
              onChange={handleChange}
              className="mt-1 block w-full rounded-lg border border-indigo-300 bg-[#0A1030] text-indigo-100 shadow-sm focus:ring-indigo-500 focus:border-indigo-200 sm:text-sm h-14"
              style={{ backgroundColor: '#0A1030' }}
            >
              <option className="bg-[#0A1030] text-indigo-100" value="Support">Support</option>
              <option className="bg-[#0A1030] text-indigo-100" value="Collaboration">Collaboration</option>
              <option className="bg-[#0A1030] text-indigo-100" value="Blessing">Blessing</option>
              <option className="bg-[#0A1030] text-indigo-100" value="Other">Other</option>
            </select>
            {formData.reason === 'Other' && (
              <div>
                <input
                  type="text"
                  name="customReason"
                  value={formData.customReason}
                  onChange={handleChange}
                  placeholder="Please specify your reason"
                  maxLength="200"
                  className="mt-3 block w-full rounded-lg border border-indigo-300 bg-[#0A1030] text-indigo-100 placeholder-indigo-400 placeholder:text-sm h-14 pl-2 shadow-sm focus:ring-indigo-500 focus:border-indigo-200 sm:text-sm"
                />
              </div>
            )}
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-indigo-100 mb-6">Subject *</label>
            <input
              type="text"
              name="subject"
              required
              value={formData.subject}
              onChange={handleChange}
              placeholder="Subject of your message"
              maxLength="100"
              className="mt-1 block w-full rounded-lg border border-indigo-300 bg-[#0A1030] text-indigo-100 placeholder-indigo-400 placeholder:text-sm h-14 pl-2 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-indigo-50 mb-6">Message *</label>
            <textarea
              name="message"
              rows="5"
              required
              value={formData.message}
              onChange={handleChange}
              placeholder="Type your message here..."
              maxLength="1500"
              className="mt-1 block w-full rounded-lg border border-indigo-300 bg-[#0A1030] text-indigo-100 placeholder-indigo-400 placeholder:text-sm h-28 pl-2 pt-3 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
            <div className={`mt-1 text-xs text-right ${
              formData.message.length > 1400 ? 'text-red-400' : 
              formData.message.length > 1200 ? 'text-yellow-400' : 
              'text-indigo-400'
            }`}>
              {formData.message.length}/1,500 characters
            </div>
            
            {/* Security Warning for Message Field */}
            {securityWarnings.message && (
              <div className="mt-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <span>🛡️</span>
                  <span className="font-semibold">
                    {securityWarnings.message.riskLevel === 'high' ? 'Security Alert' : 'Security Warning'}
                  </span>
                </div>
                <p className="text-red-300 text-sm mt-1">
                  {securityWarnings.message.riskLevel === 'high' 
                    ? 'Your message contains potentially dangerous content that will be blocked.'
                    : 'Suspicious patterns detected in your message. Please review your content.'
                  }
                </p>
                {securityWarnings.message.threats && securityWarnings.message.threats.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {securityWarnings.message.threats.slice(0, 3).map((threat, index) => (
                      <div key={index} className="text-red-300/80 text-xs">
                        • {threat.type?.replace(/_/g, ' ')}: {threat.match || threat.word || 'detected pattern'}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-indigo-100 mb-2">Attach a file (optional)</label>
            <p className="text-xs text-indigo-300 mb-4">Max 5MB. Supported: Images, PDFs, Text files, Word documents</p>
            
            <div className="relative">
              <label htmlFor="file-upload" className="cursor-pointer inline-flex items-center px-4 py-2 border border-indigo-300 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-indigo-100 font-medium transition-colors">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                Choose file
              </label>
              <input
                id="file-upload"
                type="file"
                name="file"
                onChange={handleChange}
                className="sr-only"
                accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt,.doc,.docx"
              />
            </div>
            
            {formData.file && (
              <div className="mt-3 p-3 bg-indigo-800/20 border border-indigo-600 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1 min-w-0">
                    <svg className="w-4 h-4 mr-2 text-indigo-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-sm text-indigo-200 truncate">{formData.file.name}</span>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <div className="text-xs text-indigo-300 whitespace-nowrap">
                      {(formData.file.size / 1024 / 1024).toFixed(1)}MB
                    </div>
                    <button
                      type="button"
                      onClick={removeFile}
                      className="text-red-400 hover:text-red-300 transition-colors p-1 hover:bg-red-400/10 rounded"
                      title="Remove file"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Legal Compliance Checkbox */}
          <div className="flex items-start mt-6 mb-4">
            <div className="flex items-center h-5">
              <input
                id="agree"
                name="agree"
                type="checkbox"
                checked={formData.agree}
                onChange={handleChange}
                required
                className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="agree" className="font-medium text-indigo-100">
                I agree to the <a href="/terms" className="underline text-indigo-300 hover:text-indigo-200" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="/privacy" className="underline text-indigo-300 hover:text-indigo-200" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </label>
            </div>
          </div>

          {/* Honeypot fields - hidden from humans, visible to bots */}
          <div style={{ display: 'none' }}>
            <input
              type="text"
              name="website"
              value={formData.website}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          {/* Submit Button */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 px-6 rounded-lg text-lg shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-opacity-50 ${
                isSubmitting
                  ? 'bg-indigo-400 cursor-not-allowed text-indigo-100' 
                  : 'bg-indigo-600 hover:bg-indigo-700 text-indigo-50 hover:shadow-lg'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-indigo-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending Message...
                </span>
              ) : (
                'Send Message'
              )}
            </button>
          </div>
        </form>
      </div>
      <AwakenCard />

    </div>
  );
}
